import React, { useContext, useEffect, useState } from 'react'

import { cloneDeep } from 'lodash'
import { useParams } from 'react-router'
import clsx from 'clsx'

import ViewsLoader from '../../../../../components/loader/ViewsLoader'

import {
  selectFilteredPaperworkDocuments,
  selectPaperworkTabData,
  setSendMailLoading,
} from '../../../../../store/registration/registrationSlice'
import { IRiderTeamMemberInterface } from '../../../../../models/rider-team-member/riderTeamMember.interface'
import { createMailLog } from '../../../../../helpers/sign'
import { getAllClauses } from '../../../../../helpers/clauses'

import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectedEvent } from '../../../../../store/events/eventsSlice'

import useTeamMembers from '../../../../../hooks/useTeamMembers'

import { HandleModalContext } from '../../../../../layout/mainlayout/MainLayout'

import { MODAL_CONSTS } from '../../../../../const/modal-const'
import {
  DOCUMENT_TYPES,
  getPaperWorkIcon,
  IDocument,
} from '../../../../../fakeData/fakeDocumentList'

import ITypes from '../../../event-registration-tabs/hooks/useEventRegistrationTabs.types'
import { ILightboxProps } from '../../../../../types/competitor_types'
import { mergeRidersTeamMembersWithPaperworkTabData } from '../../../../../helpers/teamMembers'

interface IProps {
  index: number
  isSigned: boolean
  loading: boolean
  disabled: boolean
  paperwork: IDocument
  ridersTeamMembers: IRiderTeamMemberInterface[]
  handleLightBox: (props: ILightboxProps) => void
}

export const Paperwork: React.FC<IProps> = ({
  index,
  isSigned,
  paperwork,
  loading,
  disabled,
  ridersTeamMembers,
  handleLightBox,
}) => {
  const dispatch = useAppDispatch()
  const { eventId, userId } = useParams<ITypes['IParams']>()

  const handleModalContext = useContext(HandleModalContext)
  const handleModal = handleModalContext?.handleModal

  const { saveCartRidersTeamMembers } = useTeamMembers()

  const filteredPaperworksDocuments = useAppSelector(selectFilteredPaperworkDocuments)
  const paperworkTabData = useAppSelector(selectPaperworkTabData)
  const { EventDetails } = useAppSelector(selectedEvent)

  const [allClauses, setAllClauses] = useState<any[]>([])

  const handleSendMail = async (filteredPaperworkIndex: number) => {
    dispatch(
      setSendMailLoading({
        sendMailLoading: true,
        sendMailIndex: filteredPaperworkIndex,
      })
    )

    try {
      const ridersTeamMembersUpdated: IRiderTeamMemberInterface[] = cloneDeep(ridersTeamMembers)

      const userTeamMembersWithMailLog = createMailLog({
        ridersTeamMembers: ridersTeamMembersUpdated,
        teamMemberId: userId,
        paperworkDocument: filteredPaperworksDocuments[filteredPaperworkIndex],
      })

      const paperworkTabDataUpdated = mergeRidersTeamMembersWithPaperworkTabData({
        paperworkTabData,
        ridersTeamMembers: userTeamMembersWithMailLog,
      })

      await saveCartRidersTeamMembers(userTeamMembersWithMailLog, paperworkTabDataUpdated)
    } catch (error) {
      console.error(error, 'error')
    } finally {
      setTimeout(() => {
        dispatch(
          setSendMailLoading({
            sendMailLoading: false,
            sendMailIndex: null,
          })
        )
      }, 1000)
    }
  }

  /** @info Opens lightbox with paperwork document image */
  const openLightBox = (images: string | string[]) => {
    let paperworkImages = images ?? []

    if (!Array.isArray(paperworkImages)) paperworkImages = [paperworkImages]

    const slides = paperworkImages.reduce((acc: any[], current: string) => {
      acc.push({ src: current, title: filteredPaperworksDocuments[index]?.document_name })
      return acc
    }, [])

    handleLightBox({
      visible: true,
      slides,
    })
  }

  /**
   * @info Sends mail by taking out the signatories from the document
   */
  useEffect(() => {
    if (EventDetails?.owner)
      getAllClauses(EventDetails.owner).then((data) => {
        if (data) setAllClauses(data)
      })
  }, [EventDetails])

  return (
    <div className="w-full relative p-5 border-SeabiscuitGray500ThemeColor border rounded-[8px] bg-white">
      <div className="flex flex-col sm:flex-row items-center gap-4 pb-5 border-SeabiscuitGray500ThemeColor border-b">
        <div className={'w-[45px] h-[45px] rounded-full flex items-center justify-center'}>
          <img
            className={'w-full h-auto'}
            src={getPaperWorkIcon(paperwork.document_type)}
            alt={paperwork.document_name}
          />
        </div>
        <div className="flex flex-col justify-center gap-0.5">
          <h4 className={'text-[14px]'}>{paperwork.document_name}</h4>
          <div className={'flex flex-wrap text-[12px] gap-x-2 gap-y-0.5 items-center'}>
            <button
              onClick={() => openLightBox(paperwork.document_image)}
              className="text-SeabiscuitDark200ThemeColor/50 underline hover:no-underline"
            >
              View document
            </button>
            {paperwork.document_type === DOCUMENT_TYPES.GENERAL && allClauses.length > 0 && (
              <>
                <div className={'h-[10px] w-[1px] bg-SeabiscuitDark200ThemeColor/50'} />
                <button
                  onClick={() => {
                    handleModal?.(true, MODAL_CONSTS.REVIEW_ANNEX, {
                      data: {
                        eventId: eventId,
                        eventName: EventDetails?.competitionName,
                      },
                    })
                    handleModalContext?.handleModal(false, MODAL_CONSTS.SIGN_DOCUMENT)
                  }}
                  className="text-SeabiscuitDark200ThemeColor/50 underline hover:no-underline"
                >
                  View Annex
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <div className={'mt-5'}>
        <p className={'text-[12px]'}>
          {paperwork.document_type === DOCUMENT_TYPES.GENERAL && allClauses.length > 0
            ? 'By clicking sign, you agree you have read and agree to the terms and conditions in this document and the attached annex.'
            : 'By clicking sign, you agree you have read and agree to the terms and conditions in this document.'}
        </p>
      </div>
      <div className={'mt-5'}>
        <button
          disabled={disabled}
          onClick={() => !isSigned && handleSendMail(index)}
          className={clsx(
            'w-full  rounded-[8px] h-[43px] text-[14px] transition-all disabled:pointer-events-none disabled:opacity-70',
            {
              'cursor-default': isSigned,
              'bg-SeabiscuitMainThemeColor text-white hover:bg-SeabiscuitMainThemeColorDark':
                !isSigned,
              'bg-SeabiscuitGreenThemeColor/15 text-SeabiscuitGreenThemeColor': isSigned,
            }
          )}
        >
          {isSigned ? 'Signed' : 'Sign'}
        </button>
      </div>
      {loading && (
        <div className={'absolute top-0 left-0 w-full h-full bg-white/70 rounded-[8px]'}>
          <ViewsLoader size={'sm'} />
        </div>
      )}
    </div>
  )
}
